import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiCopy, FiCheck, FiBook, FiTerminal, FiFileText, FiUpload, FiCode } from 'react-icons/fi';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import toast from 'react-hot-toast';

/* =============================================
   COPY CODE BLOCK
   ============================================= */
function CodeBlock({ code, language = 'bash', title }: { code: string; language?: string; title?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="terminal-block mb-4">
      <div className="terminal-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="terminal-dot bg-red-500/80" />
          <div className="terminal-dot bg-yellow-500/80" />
          <div className="terminal-dot bg-green-500/80" />
          {title && <span className="text-[10px] text-slate-500 ml-2">{title}</span>}
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-brand-400 transition-colors px-2 py-1 rounded-md hover:bg-white/5"
        >
          {copied ? <FiCheck size={10} className="text-green-400" /> : <FiCopy size={10} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <SyntaxHighlighter
        language={language}
        style={vscDarkPlus}
        customStyle={{
          margin: 0,
          padding: '16px 20px',
          background: '#000',
          fontSize: '13px',
          lineHeight: '1.6',
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

/* =============================================
   ACCORDION SECTION
   ============================================= */
function AccordionSection({
  title,
  icon,
  children,
  defaultOpen = false,
  id,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  id?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div id={id} className="glass-card overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-6 py-5 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-brand-400 shrink-0">
          {icon}
        </div>
        <span className="text-lg font-semibold text-white flex-1">{title}</span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <FiChevronDown size={18} className="text-slate-500" />
        </motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 space-y-4 text-slate-300 text-sm leading-relaxed border-t border-white/5 pt-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* =============================================
   DOCS PAGE
   ============================================= */
export default function Docs() {
  return (
    <div className="pt-24 pb-16 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            <span className="gradient-text">Documentation</span>
          </h1>
          <p className="text-slate-400 text-lg">
            Everything you need to discover, install, and create AI skills.
          </p>
        </motion.div>

        {/* Quick Nav */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 mb-8"
        >
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-4">Quick Navigation</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              { label: 'Getting Started', href: '#getting-started', icon: <FiBook size={14} /> },
              { label: 'Installing', href: '#installing', icon: <FiTerminal size={14} /> },
              { label: 'Creating', href: '#creating', icon: <FiCode size={14} /> },
              { label: 'Publishing', href: '#publishing', icon: <FiUpload size={14} /> },
              { label: 'SKILL.md', href: '#skill-format', icon: <FiFileText size={14} /> },
            ].map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium text-slate-400 hover:text-brand-400 hover:bg-white/5 transition-all"
              >
                {item.icon}
                {item.label}
              </a>
            ))}
          </div>
        </motion.div>

        {/* Sections */}
        <div className="space-y-4">
          {/* Getting Started */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <AccordionSection
              title="Getting Started"
              icon={<FiBook size={16} />}
              defaultOpen={true}
              id="getting-started"
            >
              <h3 className="text-xl font-semibold text-white mt-2">What are Skills AI?</h3>
              <p>
                Skills AI are reusable, shareable instruction sets that enhance AI coding agents like
                Claude Code, Cursor, Windsurf, Codex, and 40+ other agents. They teach your AI agent
                specific capabilities such as code review, test generation, documentation writing, and more.
              </p>

              <h3 className="text-xl font-semibold text-white mt-6">How They Work</h3>
              <p>
                Skills are defined in a <code className="text-brand-400 bg-black/50 px-1.5 py-0.5 rounded text-xs">SKILL.md</code> file
                that contains structured instructions, examples, and configuration. When installed, the skill's
                instructions are injected into your AI agent's context, giving it new capabilities.
              </p>

              <div className="glass p-4 rounded-xl mt-4">
                <h4 className="text-sm font-semibold text-white mb-2">Key Concepts</h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex gap-2">
                    <span className="text-brand-400 shrink-0">1.</span>
                    <span><strong className="text-white">Skills</strong> are markdown files with structured instructions for AI agents</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-brand-400 shrink-0">2.</span>
                    <span><strong className="text-white">Agents</strong> are AI coding assistants (Claude Code, Cursor, etc.)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-brand-400 shrink-0">3.</span>
                    <span><strong className="text-white">CLI</strong> (<code className="text-brand-400 bg-black/50 px-1 rounded text-xs">npx skillsai</code>) manages skill installation</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-brand-400 shrink-0">4.</span>
                    <span><strong className="text-white">Registry</strong> is this directory where skills are published and discovered</span>
                  </li>
                </ul>
              </div>
            </AccordionSection>
          </motion.div>

          {/* Installing Skills */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <AccordionSection
              title="Installing Skills"
              icon={<FiTerminal size={16} />}
              id="installing"
            >
              <h3 className="text-xl font-semibold text-white">Install a Skill</h3>
              <p>
                Use the <code className="text-brand-400 bg-black/50 px-1.5 py-0.5 rounded text-xs">aiskills</code> CLI
                to install any skill from the directory. Specify which agent you're using:
              </p>

              <CodeBlock
                code={`# Install a skill for Claude Code
npx skillsai add strongly-ai/code-review --agent claude-code

# Install for Cursor
npx skillsai add strongly-ai/code-review --agent cursor

# Install for Windsurf
npx skillsai add strongly-ai/code-review --agent windsurf

# Install for any agent
npx skillsai add strongly-ai/code-review --agent codex`}
                title="install-skill.sh"
              />

              <h3 className="text-xl font-semibold text-white mt-6">List Installed Skills</h3>
              <CodeBlock
                code={`# List all installed skills
npx skillsai list

# List skills for a specific agent
npx skillsai list --agent cursor`}
                title="list-skills.sh"
              />

              <h3 className="text-xl font-semibold text-white mt-6">Remove a Skill</h3>
              <CodeBlock
                code={`# Remove a skill
npx skillsai remove strongly-ai/code-review

# Remove for a specific agent only
npx skillsai remove strongly-ai/code-review --agent cursor`}
                title="remove-skill.sh"
              />

              <h3 className="text-xl font-semibold text-white mt-6">Update Skills</h3>
              <CodeBlock
                code={`# Update all installed skills
npx skillsai update

# Update a specific skill
npx skillsai update strongly-ai/code-review`}
                title="update-skills.sh"
              />

              <div className="glass p-4 rounded-xl mt-4">
                <h4 className="text-sm font-semibold text-yellow-400 mb-2">Where do skills get installed?</h4>
                <p className="text-sm text-slate-400">
                  Skills are installed to agent-specific locations. For Claude Code, they go into your
                  project's <code className="text-brand-400 bg-black/50 px-1 rounded text-xs">.claude/</code> directory.
                  For Cursor, they go into <code className="text-brand-400 bg-black/50 px-1 rounded text-xs">.cursor/rules/</code>.
                  Each agent has its own convention. The CLI handles placement automatically.
                </p>
              </div>
            </AccordionSection>
          </motion.div>

          {/* Creating Skills */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <AccordionSection
              title="Creating Skills"
              icon={<FiCode size={16} />}
              id="creating"
            >
              <h3 className="text-xl font-semibold text-white">Create Your First Skill</h3>
              <p>
                A skill is a GitHub repository with a <code className="text-brand-400 bg-black/50 px-1.5 py-0.5 rounded text-xs">SKILL.md</code> file
                at the root. The file uses standard Markdown with YAML frontmatter for metadata.
              </p>

              <h4 className="text-lg font-semibold text-white mt-6">Step 1: Create a Repository</h4>
              <p>
                Create a new GitHub repository for your skill. The repository name will become the skill's identifier.
              </p>

              <CodeBlock
                code={`# Create a new directory
mkdir my-awesome-skill
cd my-awesome-skill
git init

# Create the SKILL.md file
touch SKILL.md`}
                title="create-repo.sh"
              />

              <h4 className="text-lg font-semibold text-white mt-6">Step 2: Write SKILL.md</h4>
              <p>
                The SKILL.md file defines your skill's instructions. See the SKILL.md Format section for the full specification.
              </p>

              <CodeBlock
                code={`---
name: my-awesome-skill
description: A brief description of what this skill does
version: 1.0.0
author: your-username
categories:
  - code-quality
  - automation
tags:
  - lint
  - review
compatible_agents:
  - claude-code
  - cursor
  - windsurf
---

# My Awesome Skill

## Description
Detailed description of what this skill does and how it helps.

## Instructions
When asked to perform [task], follow these steps:

1. First, analyze the codebase structure
2. Then, identify areas for improvement
3. Finally, provide actionable suggestions

## Examples
### Example 1: Basic Usage
\`\`\`
User: Review this file for issues
Agent: [uses skill instructions to review]
\`\`\`

## Configuration
Optional configuration options for customization.`}
                language="markdown"
                title="SKILL.md"
              />

              <h4 className="text-lg font-semibold text-white mt-6">Step 3: Test Locally</h4>
              <CodeBlock
                code={`# Install your skill locally
npx skillsai add ./my-awesome-skill --agent claude-code

# Or link for development
npx skillsai link . --agent claude-code`}
                title="test-local.sh"
              />
            </AccordionSection>
          </motion.div>

          {/* Publishing */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <AccordionSection
              title="Publishing"
              icon={<FiUpload size={16} />}
              id="publishing"
            >
              <h3 className="text-xl font-semibold text-white">Publish to the Directory</h3>
              <p>
                Once your skill is ready, publish it to the Skills AI directory from the website so others
                can discover and install it. Publishing is done on the web - the CLI is for finding and
                installing skills, so it never needs your account.
              </p>

              <h4 className="text-lg font-semibold text-white mt-6">Publish from the web</h4>
              <ol className="space-y-2 text-sm text-slate-400 list-decimal ml-5">
                <li>Push your skill to a public GitHub repository</li>
                <li>Sign in and go to the <a href="/submit" className="text-brand-400 hover:text-brand-300">Submit page</a></li>
                <li>Enter your repository URL</li>
                <li>Click "Detect" to parse your SKILL.md</li>
                <li>Select categories and compatible agents</li>
                <li>Submit for review</li>
              </ol>

              <div className="glass p-4 rounded-xl mt-4">
                <h4 className="text-sm font-semibold text-white mb-2">Updating a Published Skill</h4>
                <p className="text-sm text-slate-400">
                  To update, simply push changes to your GitHub repository. The directory automatically
                  pulls the latest version of your SKILL.md, so there is nothing else to run.
                </p>
              </div>
            </AccordionSection>
          </motion.div>

          {/* SKILL.md Format */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <AccordionSection
              title="SKILL.md Format Specification"
              icon={<FiFileText size={16} />}
              id="skill-format"
            >
              <h3 className="text-xl font-semibold text-white">Full Specification</h3>
              <p>
                The SKILL.md file uses YAML frontmatter followed by Markdown content. Here's the complete specification:
              </p>

              <h4 className="text-lg font-semibold text-white mt-6">Frontmatter Fields</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm mt-3">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-3 text-slate-400 font-medium">Field</th>
                      <th className="text-left py-2 px-3 text-slate-400 font-medium">Type</th>
                      <th className="text-left py-2 px-3 text-slate-400 font-medium">Required</th>
                      <th className="text-left py-2 px-3 text-slate-400 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    <tr className="border-b border-white/5">
                      <td className="py-2 px-3 font-mono text-brand-400 text-xs">name</td>
                      <td className="py-2 px-3">string</td>
                      <td className="py-2 px-3 text-green-400">Yes</td>
                      <td className="py-2 px-3">Unique skill identifier (kebab-case)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2 px-3 font-mono text-brand-400 text-xs">description</td>
                      <td className="py-2 px-3">string</td>
                      <td className="py-2 px-3 text-green-400">Yes</td>
                      <td className="py-2 px-3">Short description (max 200 chars)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2 px-3 font-mono text-brand-400 text-xs">version</td>
                      <td className="py-2 px-3">string</td>
                      <td className="py-2 px-3 text-green-400">Yes</td>
                      <td className="py-2 px-3">Semantic version (e.g., 1.0.0)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2 px-3 font-mono text-brand-400 text-xs">author</td>
                      <td className="py-2 px-3">string</td>
                      <td className="py-2 px-3 text-green-400">Yes</td>
                      <td className="py-2 px-3">Author username</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2 px-3 font-mono text-brand-400 text-xs">categories</td>
                      <td className="py-2 px-3">string[]</td>
                      <td className="py-2 px-3 text-yellow-400">Recommended</td>
                      <td className="py-2 px-3">Category tags</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2 px-3 font-mono text-brand-400 text-xs">tags</td>
                      <td className="py-2 px-3">string[]</td>
                      <td className="py-2 px-3 text-slate-500">No</td>
                      <td className="py-2 px-3">Search keywords</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2 px-3 font-mono text-brand-400 text-xs">compatible_agents</td>
                      <td className="py-2 px-3">string[]</td>
                      <td className="py-2 px-3 text-yellow-400">Recommended</td>
                      <td className="py-2 px-3">List of compatible agent identifiers</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h4 className="text-lg font-semibold text-white mt-8">Markdown Content Sections</h4>
              <div className="space-y-3 mt-3">
                {[
                  { title: '# Title', desc: 'The skill name as an H1 heading' },
                  { title: '## Description', desc: 'Detailed explanation of the skill\'s purpose and capabilities' },
                  { title: '## Instructions', desc: 'Step-by-step instructions the AI agent should follow' },
                  { title: '## Examples', desc: 'Usage examples showing input/output pairs' },
                  { title: '## Configuration', desc: 'Optional configuration parameters' },
                  { title: '## Constraints', desc: 'Any limitations or constraints the agent should respect' },
                ].map((section) => (
                  <div key={section.title} className="flex gap-3 items-start">
                    <code className="text-brand-400 bg-black/50 px-2 py-0.5 rounded text-xs font-mono shrink-0 mt-0.5">
                      {section.title}
                    </code>
                    <span className="text-slate-400 text-sm">{section.desc}</span>
                  </div>
                ))}
              </div>

              <h4 className="text-lg font-semibold text-white mt-8">Complete Example</h4>
              <CodeBlock
                code={`---
name: code-review
description: Comprehensive code review with security and performance analysis
version: 1.2.0
author: strongly-ai
categories:
  - code-quality
  - security
  - performance
tags:
  - review
  - lint
  - best-practices
compatible_agents:
  - claude-code
  - cursor
  - windsurf
  - codex
  - copilot
  - aider
---

# Code Review Skill

## Description
A comprehensive code review skill that analyzes code for security
vulnerabilities, performance bottlenecks, and adherence to best practices.

## Instructions
When asked to review code, follow this process:

1. **Security Analysis**
   - Check for SQL injection, XSS, CSRF vulnerabilities
   - Look for hardcoded secrets or credentials
   - Verify input validation and sanitization

2. **Performance Review**
   - Identify N+1 queries and unnecessary database calls
   - Check for memory leaks and resource cleanup
   - Look for missing caching opportunities

3. **Code Quality**
   - Verify naming conventions and code organization
   - Check for proper error handling
   - Ensure adequate test coverage

## Examples
### Security Issue Detection
\\\`\\\`\\\`javascript
// BAD: SQL injection vulnerability
const query = \\\`SELECT * FROM users WHERE id = \${userId}\\\`;

// GOOD: Parameterized query
const query = 'SELECT * FROM users WHERE id = $1';
const result = await db.query(query, [userId]);
\\\`\\\`\\\`

## Configuration
- \\\`severity\\\`: Set to "strict", "moderate", or "lenient"
- \\\`focus\\\`: Array of focus areas ["security", "performance", "style"]
- \\\`language\\\`: Primary language hint for better analysis`}
                language="markdown"
                title="SKILL.md"
              />
            </AccordionSection>
          </motion.div>

          {/* CLI Reference */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <AccordionSection
              title="CLI Reference"
              icon={<FiTerminal size={16} />}
              id="cli"
            >
              <h3 className="text-xl font-semibold text-white">Command Reference</h3>

              <div className="space-y-6 mt-4">
                {[
                  {
                    cmd: 'npx skillsai add <owner>/<skill> --agent <agent>',
                    desc: 'Install a skill from the directory',
                    flags: ['--agent <name>: Target agent (required)', '--global: Install globally', '--force: Overwrite existing'],
                  },
                  {
                    cmd: 'npx skillsai remove <owner>/<skill>',
                    desc: 'Remove an installed skill',
                    flags: ['--agent <name>: Remove for specific agent only'],
                  },
                  {
                    cmd: 'npx skillsai list',
                    desc: 'List all installed skills',
                    flags: ['--agent <name>: Filter by agent', '--json: Output as JSON'],
                  },
                  {
                    cmd: 'npx skillsai update [owner/skill]',
                    desc: 'Update installed skills to latest version',
                    flags: ['--all: Update all skills'],
                  },
                  {
                    cmd: 'npx skillsai search <query>',
                    desc: 'Search the skills directory',
                    flags: ['--category <name>: Filter by category', '--agent <name>: Filter by agent'],
                  },
                  {
                    cmd: 'npx skillsai link .',
                    desc: 'Symlink local skill for development',
                    flags: ['--agent <name>: Target agent'],
                  },
                ].map((item) => (
                  <div key={item.cmd} className="glass p-4 rounded-xl">
                    <code className="text-brand-400 text-sm font-mono">{item.cmd}</code>
                    <p className="text-slate-400 text-sm mt-1">{item.desc}</p>
                    {item.flags.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {item.flags.map((flag) => (
                          <div key={flag} className="text-xs text-slate-500 flex items-center gap-1.5">
                            <span className="text-slate-600">--</span>
                            <code className="text-slate-400">{flag}</code>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </AccordionSection>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
